import { useState } from "react";
import { DataSourceCardProps, Filter } from "../../../components/marketing/audience/type";
import { dataSources } from "../data";
import { FilterComponent } from "../../../components/marketing/audience/filter-component";
import { ChevronDown, ChevronUp, Eye, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CustomerData {
  id: number;
  name: string;
  email: string;
  age: number;
  ltv: number;
  status: string;
}

interface ProductData {
  id: number;
  product: string;
  category: string;
  price: number;
  units_sold: number;
}

interface BehavioralData {
  id: number;
  user_id: number;
  action: string;
  timestamp: string;
  duration?: number;
  product_id?: number;
  value?: number;
}
interface AudiencePreview {
  id: number;
  name: string;
  source: string;
  filters: Filter[]; // ✅ match your state
  combineWith: string;
  estimatedSize: number;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  dataSource,
  isSelected,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`p-4 border-2 rounded-lg text-left transition-colors ${
      isSelected
        ? "border-purple-500 bg-purple-50"
        : "border-gray-200 hover:border-gray-300"
    }`}
  >
    <div className="font-semibold text-gray-900">{dataSource.name}</div>
    <div className="text-sm text-gray-500">{dataSource.fields} fields</div>
  </button>
);

const NewAudience: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"source" | "rules">("source");
  const [audienceName, setAudienceName] = useState("");
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [estimatedSize, setEstimatedSize] = useState(120000);
  const [combineWith, setCombineWith] = useState("AND");
  const [filters, setFilters] = useState<Filter[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<AudiencePreview[]>([]);

  const addFilter = () => {
    const newFilter: Filter = {
      id: Date.now().toString(),
      field: "age",
      operator: "equals",
      value: "",
    };
    setFilters([...filters, newFilter]);
    setEstimatedSize(60000);
  };

  const updateFilter = (id: string, updatedFilter: Filter) => {
    setFilters(filters.map((f) => (f.id === id ? updatedFilter : f)));
    // Recalculate estimated size based on filter changes
    updateEstimatedSize();
  };

  const removeFilter = (id: string) => {
    setFilters(filters.filter((f) => f.id !== id));
    if (filters.length === 1) {
      setEstimatedSize(120000);
    }
    updateEstimatedSize();
  };

  const updateEstimatedSize = () => {
    // You can implement logic here to calculate estimated size based on filters
    // For now, using a simple calculation
    const baseSize = 120000;
    const reduction = filters.length * 20000;
    setEstimatedSize(Math.max(baseSize - reduction, 1000));
  };

  const handleSourceSelect = (sourceName: string) => {
    setSelectedSource(sourceName);
    setStep("rules");
    // Clear preview data when source changes
    setPreviewData([]);
  };

  function backToAudience() {
    navigate("/audience");
  }

const handleCreateAudience = () => {
  const newAudience: AudiencePreview = {
    id: Date.now(),
    name: audienceName,
    source: selectedSource,
    filters,
    combineWith,
    estimatedSize,
  };

  // Append instead of replacing
  setPreviewData((prev) => [...prev, newAudience]);
  setShowPreview(true); 
};


  const applyFilterCondition = (value: any, filter: Filter) => {
    if (filter.operator === "equals") {
      return String(value).toLowerCase() === String(filter.value).toLowerCase();
    } else if (filter.operator === "contains") {
      return String(value)
        .toLowerCase()
        .includes(String(filter.value).toLowerCase());
    } else if (filter.operator === "greater_than") {
      const numValue = Number(value);
      const numFilterValue = Number(filter.value);
      return (
        !isNaN(numValue) && !isNaN(numFilterValue) && numValue > numFilterValue
      );
    } else if (filter.operator === "less_than") {
      const numValue = Number(value);
      const numFilterValue = Number(filter.value);
      return (
        !isNaN(numValue) && !isNaN(numFilterValue) && numValue < numFilterValue
      );
    }
    return true;
  };

  // Apply filters to preview data
  const getFilteredPreview = () => {
    if (previewData.length === 0 || filters.length === 0) return previewData;

    return previewData.filter((item: Record<string, any>) => {
      if (combineWith === "AND") {
        return filters.every((filter) => {
          const fieldName = filter.field.toLowerCase();
          const value = item[fieldName];
          if (value === undefined) return false;
          return applyFilterCondition(value, filter);
        });
      } else {
        return filters.some((filter) => {
          const fieldName = filter.field.toLowerCase();
          const value = item[fieldName];
          if (value === undefined) return false;
          return applyFilterCondition(value, filter);
        });
      }
    });
  };

  const filteredPreviewData = getFilteredPreview();

  const handlePreviewToggle = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div>
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">New Audience</h2>
        <button className="text-gray-500 hover:text-gray-700 transition-colors">
          <X onClick={backToAudience} className="w-6 h-6" />
        </button>
      </div>

      <div className="p-6">
        {/* Estimated Audience */}
        <div className="mb-6 p-4 bg-blue-100 rounded-lg">
          <div className="text-sm text-blue-700 mb-1">ESTIMATED AUDIENCE</div>
          <div className="text-2xl font-bold text-blue-900">
            {estimatedSize.toLocaleString()}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setStep("source")}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              step === "source"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Data Source
          </button>
          <button
            onClick={() => setStep("rules")}
            disabled={!selectedSource}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              step === "rules"
                ? "bg-white text-gray-900 shadow-sm"
                : selectedSource
                ? "text-gray-600 hover:text-gray-900"
                : "text-gray-400 cursor-not-allowed"
            }`}
          >
            Rules
          </button>
        </div>

        <div className="min-h-[400px] overflow-y-auto">
          {step === "source" && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience name
                </label>
                <input
                  type="text"
                  placeholder="e.g. High LTV Shoppers"
                  value={audienceName}
                  onChange={(e) => setAudienceName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select a data source
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {dataSources.map((source) => (
                    <DataSourceCard
                      key={source.name}
                      dataSource={source}
                      isSelected={selectedSource === source.name}
                      onClick={() => handleSourceSelect(source.name)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === "rules" && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience name
                </label>
                <input
                  type="text"
                  placeholder="e.g. High LTV Shoppers"
                  value={audienceName}
                  onChange={(e) => setAudienceName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 outline-none"
                />
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Source:</span>
                  <span className="font-medium text-gray-900">
                    {selectedSource}
                  </span>
                </div>
                <div className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  Est. {estimatedSize.toLocaleString()}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">
                    Combine filters with
                  </span>
                  <select
                    value={combineWith}
                    onChange={(e) => setCombineWith(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded focus:ring-2"
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                {filters.map((filter) => (
                  <FilterComponent
                    key={filter.id}
                    filter={filter}
                    onUpdate={(updatedFilter) =>
                      updateFilter(filter.id, updatedFilter)
                    }
                    onRemove={() => removeFilter(filter.id)}
                  />
                ))}
              </div>

              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={addFilter}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add filter
                </button>

                <button
                  onClick={handlePreviewToggle}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  <Eye className="w-4 h-4" />
                  Preview data
                  {showPreview ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>

              {showPreview && (
                <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700">
                    Preview ({previewData.length} audience
                    {previewData.length > 1 ? "s" : ""})
                  </div>

                  {previewData.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/5">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-1/6">
                              Source
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-2/5">
                              Filters
                            </th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-1/12">
                              Combine
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider w-1/6">
                              Est. Size
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {previewData.map((aud) => (
                            <tr key={aud.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-900">
                                {aud.name}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                {aud.source}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                <ul className="space-y-1">
                                  {aud.filters.map((f) => (
                                    <li
                                      key={f.id}
                                      className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-xs text-gray-600"
                                    >
                                      {f.field} {f.operator} {f.value}
                                    </li>
                                  ))}
                                </ul>
                              </td>
                              <td className="px-4 py-2 text-center text-gray-700">
                                <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                                  {aud.combineWith}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                {aud.estimatedSize.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No audience created yet
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
        <button
          onClick={backToAudience}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleCreateAudience}
          disabled={!audienceName || !selectedSource}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
        >
          Create Audience
        </button>
      </div>
    </div>
  );
};

export default NewAudience;
